// PaymentMethodsModal.jsx
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SUPPORT_LINKS, PAYMENT_MODAL_CONFIG } from '../../config/appConfig';
import { useLanguage } from '../../contexts/LanguageContext';

const anim = {
  initial: { opacity: 0.6 },
  animate: { opacity: 1 },
  transition: { duration: 0.1, ease: 'easeOut' },
};

// ═══ Sleek Vector Icons ═══
const CardIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="14" x="2" y="5" rx="2" />
    <line x1="2" x2="22" y1="10" y2="10" />
  </svg>
);

const SbpIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const WalletIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
    <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
  </svg>
);

const CryptoIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 6v12M9.5 8h4a2 2 0 0 1 0 4h-4a2 2 0 0 0 0 4h5" />
  </svg>
);

const SupportIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
  </svg>
);

const GemIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3h12l4 6-10 12L2 9z" />
    <path d="M11 3 8 9l4 12 4-12-3-6" />
    <path d="M2 9h20" />
  </svg>
);

const DiscordIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.929 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.894.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

const TelegramIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 0 0-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
  </svg>
);

const CloseIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const ArrowLeftIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ExternalLinkIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" x2="21" y1="14" y2="3" />
  </svg>
);

const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ClockIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const AlertTriangleIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const RefreshIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
    <path d="M16 21h5v-5" />
  </svg>
);

const renderMethodIcon = (method) => {
  const id = (method.id || '').toLowerCase();
  const icon = (method.icon || '').toLowerCase();
  if (method.type === 'contact_support' || id === 'contact_support' || icon.includes('comment')) {
    return <SupportIcon className="w-5 h-5" />;
  }
  if (id.includes('card') || icon.includes('card')) {
    return <CardIcon className="w-5 h-5" />;
  }
  if (id.includes('sbp') || id.includes('fast') || icon.includes('gem')) {
    return <SbpIcon className="w-5 h-5" />;
  }
  if (id.includes('yoomoney') || id.includes('money') || id.includes('wallet') || icon.includes('crown')) {
    return <WalletIcon className="w-5 h-5" />;
  }
  if (id.includes('crypto') || icon.includes('dollar') || icon.includes('bitcoin')) {
    return <CryptoIcon className="w-5 h-5" />;
  }
  return <CardIcon className="w-5 h-5" />;
};

const getPlanTheme = (plan) => {
  const key = `${plan?.id || ''} ${plan?.name || ''}`.toLowerCase();
  if (key.includes('vip')) {
    return {
      name: 'vip',
      accent: '#f59e0b',
      textAccent: 'text-amber-400',
      bannerBg: 'bg-amber-500/[0.04]',
      bannerBorder: 'border-amber-500/20',
      selectedRowBg: 'bg-amber-500/[0.07]',
      selectedRowBorder: 'border-amber-500/35',
      selectedIconText: 'text-amber-400',
      selectedIconBg: 'bg-amber-500/15',
      radioBorder: 'border-amber-500',
      radioBg: 'bg-amber-500',
      buttonBg: 'bg-[#f59e0b] hover:bg-[#fbbf24] text-black shadow-[0_4px_20px_rgba(245,158,11,0.35)]',
      topLine: 'linear-gradient(90deg, transparent, #f59e0b, transparent)',
      pulseBg: 'bg-amber-400',
    };
  }
  if (key.includes('premium')) {
    return {
      name: 'premium',
      accent: '#22c55e',
      textAccent: 'text-emerald-400',
      bannerBg: 'bg-emerald-500/[0.04]',
      bannerBorder: 'border-emerald-500/20',
      selectedRowBg: 'bg-emerald-500/[0.07]',
      selectedRowBorder: 'border-emerald-500/35',
      selectedIconText: 'text-emerald-400',
      selectedIconBg: 'bg-emerald-500/15',
      radioBorder: 'border-emerald-500',
      radioBg: 'bg-emerald-500',
      buttonBg: 'bg-[#22c55e] hover:bg-[#4ade80] text-black shadow-[0_4px_20px_rgba(34,197,94,0.35)]',
      topLine: 'linear-gradient(90deg, transparent, #22c55e, transparent)',
      pulseBg: 'bg-emerald-400',
    };
  }
  if (key.includes('pro') || key.includes('professional')) {
    return {
      name: 'professional',
      accent: '#d946ef',
      textAccent: 'text-fuchsia-400',
      bannerBg: 'bg-fuchsia-500/[0.04]',
      bannerBorder: 'border-fuchsia-500/20',
      selectedRowBg: 'bg-fuchsia-500/[0.07]',
      selectedRowBorder: 'border-fuchsia-500/35',
      selectedIconText: 'text-fuchsia-400',
      selectedIconBg: 'bg-fuchsia-500/15',
      radioBorder: 'border-fuchsia-500',
      radioBg: 'bg-fuchsia-500',
      buttonBg: 'bg-[#d946ef] hover:bg-[#e879f9] text-black shadow-[0_4px_20px_rgba(217,70,239,0.35)]',
      topLine: 'linear-gradient(90deg, transparent, #d946ef, transparent)',
      pulseBg: 'bg-fuchsia-400',
    };
  }
  return {
    name: 'default',
    accent: '#ffffff',
    textAccent: 'text-white',
    bannerBg: 'bg-white/[0.03]',
    bannerBorder: 'border-white/[0.06]',
    selectedRowBg: 'bg-white/[0.08]',
    selectedRowBorder: 'border-white/20',
    selectedIconText: 'text-white',
    selectedIconBg: 'bg-white/[0.08]',
    radioBorder: 'border-white',
    radioBg: 'bg-white',
    buttonBg: 'bg-white hover:bg-neutral-200 text-black shadow-[0_4px_20px_rgba(255,255,255,0.2)]',
    topLine: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
    pulseBg: 'bg-white',
  };
};

const PaymentMethodsModal = ({
  plan = {}, actionType = 'upgrade', methods = [], paymentDetails = null,
  onClose, onConfirmWithMethod, initialPhase, statusMessage
}) => {
  const { t, language } = useLanguage();
  const isEn = language === 'en';
  const theme = getPlanTheme(plan);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [modalPhase, setModalPhase] = useState('selecting_method');
  const [internalError, setInternalError] = useState(null);
  const [internalSuccessMessage, setInternalSuccessMessage] = useState(null);
  const [showSupportMessage, setShowSupportMessage] = useState(false);
  const [showLongWaitSupport, setShowLongWaitSupport] = useState(false);

  // === ALL LOGIC PRESERVED ===
  useEffect(() => {
    setShowSupportMessage(false);
    const timer = setTimeout(() => {
      if (['selecting_method', 'initiating_payment', 'awaiting_payment_completion'].includes(modalPhase)) setShowSupportMessage(true);
    }, PAYMENT_MODAL_CONFIG.supportMessageDelay);
    return () => clearTimeout(timer);
  }, [modalPhase]);

  useEffect(() => {
    setShowLongWaitSupport(false);
    const timer = setTimeout(() => {
      if (modalPhase === 'awaiting_payment_completion') setShowLongWaitSupport(true);
    }, 60000); // 1 minute delay
    return () => clearTimeout(timer);
  }, [modalPhase]);

  useEffect(() => {
    if (initialPhase) {
      setModalPhase(initialPhase);
      if (initialPhase === 'payment_successful' && statusMessage) { setInternalSuccessMessage(statusMessage); setInternalError(null); return; }
      else if (initialPhase === 'payment_error' && statusMessage) { setInternalError(statusMessage); setInternalSuccessMessage(null); return; }
    }
    if (paymentDetails) {
      if (paymentDetails.paymentUrl) {
        setInternalError(null);
        try {
          window.open(paymentDetails.paymentUrl, '_blank', 'noopener,noreferrer');
          if (!initialPhase || initialPhase !== 'awaiting_payment_completion') {
            if (modalPhase !== 'awaiting_payment_completion') setModalPhase('awaiting_payment_completion');
          }
        } catch (e) { setInternalError(t('couldNotProcessPaymentMethod')); if (modalPhase !== 'payment_error') setModalPhase('payment_error'); }
      } else { setInternalError(paymentDetails.message || t('failedToProcessPaymentInitiation')); if (modalPhase !== 'payment_error') setModalPhase('payment_error'); }
    } else if (!initialPhase && !['initiating_payment', 'payment_error', 'payment_successful', 'awaiting_payment_completion'].includes(modalPhase)) {
      setModalPhase('selecting_method'); setInternalError(null); setSelectedMethod(null);
    }
  }, [paymentDetails, initialPhase, statusMessage, t]);

  useEffect(() => {
    if (initialPhase) {
      setModalPhase(initialPhase);
      if (initialPhase === 'payment_successful' && statusMessage) { setInternalSuccessMessage(statusMessage); setInternalError(null); }
      else if (initialPhase === 'payment_error' && statusMessage) { setInternalError(statusMessage); setInternalSuccessMessage(null); }
    } else if (!['payment_successful', 'payment_error', 'awaiting_payment_completion', 'initiating_payment', 'contact_support'].includes(modalPhase)) {
      setModalPhase('selecting_method'); setSelectedMethod(null); setInternalError(null); setInternalSuccessMessage(null);
    }
  }, [plan, actionType, initialPhase, statusMessage, modalPhase, t]);

  const handleMethodSelect = (method) => {
    if (modalPhase !== 'selecting_method') return;
    if (method.type === 'contact_support' || method.id === 'contact_support') { setModalPhase('contact_support'); setSelectedMethod(null); return; }
    setSelectedMethod(method); setInternalError(null);
  };
  const handleConfirm = () => {
    if (modalPhase !== 'selecting_method' || !selectedMethod) return;
    setInternalError(null); setModalPhase('initiating_payment'); onConfirmWithMethod(plan, actionType, selectedMethod);
  };
  const handleBack = () => { setModalPhase('selecting_method'); setSelectedMethod(null); setInternalError(null); };
  const isBlocking = modalPhase === 'awaiting_payment_completion' || modalPhase === 'initiating_payment';

  // === RENDER ===
  return (
    <div className="relative w-full flex flex-col max-h-[85vh] sm:max-h-[calc(90vh-48px)] overflow-y-auto overscroll-contain pr-0.5 select-text">

      {/* ═══ Plan Banner — always visible ═══ */}
      <div className={`relative overflow-hidden rounded-2xl p-3 sm:p-3.5 ${theme.bannerBg} border ${theme.bannerBorder} flex items-center gap-2.5 sm:gap-3.5 mb-3 shrink-0`}>
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: theme.topLine }} />
        <div className={`w-8 h-8 flex items-center justify-center shrink-0 ${theme.textAccent}`}>
          <GemIcon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-[10px] uppercase tracking-wider font-bold ${theme.textAccent}`}>
            {actionType === 'extend' ? (isEn ? 'Renew Plan' : 'Продление тарифа') : (isEn ? 'Upgrade to' : 'Переход на')}
          </p>
          <h3 className="text-sm sm:text-lg font-bold text-white tracking-tight leading-tight truncate">
            {plan.name}
          </h3>
        </div>
        <div className="text-right shrink-0">
          <div className="text-base sm:text-xl font-bold text-white tracking-tight whitespace-nowrap">{plan.price}</div>
          <div className="text-[10px] sm:text-[11px] text-white/40">
            {plan.period ? `/${String(plan.period).replace(/^[\/\s]+/, '').trim()}` : ''}
          </div>
        </div>
      </div>

      {/* ═══ Header — always visible ═══ */}
      <div className="flex justify-between items-center mb-3 shrink-0">
        <div className="flex items-center gap-2">
          {modalPhase === 'contact_support' && (
            <button
              onClick={handleBack}
              className="w-7 h-7 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-white/60 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>
          )}
          <h2 className="text-sm font-bold text-white tracking-tight">
            {modalPhase === 'selecting_method' && (t('selectPaymentMethod') || 'Payment Method')}
            {modalPhase === 'initiating_payment' && (t('initiatingPayment') || 'Processing')}
            {modalPhase === 'awaiting_payment_completion' && (t('awaitingPaymentConfirmation') || 'Awaiting Confirmation')}
            {modalPhase === 'payment_error' && (t('paymentError') || 'Payment Error')}
            {modalPhase === 'payment_successful' && (t('paymentSuccessful') || 'Success!')}
            {modalPhase === 'contact_support' && (t('otherPaymentMethods') || 'Alternative Methods')}
          </h2>
        </div>
        <button
          onClick={onClose}
          disabled={isBlocking}
          className="w-7 h-7 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-white/50 hover:text-white flex items-center justify-center transition-colors disabled:opacity-30 disabled:pointer-events-none cursor-pointer"
        >
          <CloseIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* ═══ Phase Content ═══ */}
      <div className="flex flex-col min-h-0">

        {/* ── Select Method ── */}
        {modalPhase === 'selecting_method' && (
          <motion.div key="select" {...anim} className="flex flex-col min-h-0">
            {methods && methods.length > 0 ? (
              <>
                {/* SCROLLABLE: methods list */}
                <div className="overflow-y-auto overscroll-contain space-y-1.5 mb-3 pr-0.5 min-h-0" style={{ maxHeight: '38vh' }}>
                  {methods.map((method, index) => {
                    const isContact = method.type === 'contact_support' || method.id === 'contact_support';
                    const isSel = !isContact && selectedMethod?.id === method.id;
                    return (
                      <div
                        key={method.id || index}
                        onClick={() => handleMethodSelect(method)}
                        className={`rounded-xl p-2.5 sm:p-3 transition-colors duration-150 cursor-pointer flex items-center gap-3 select-none ${
                          isContact
                            ? 'bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.05]'
                            : isSel
                              ? `${theme.selectedRowBg} border ${theme.selectedRowBorder}`
                              : 'bg-white/[0.02] hover:bg-white/[0.05] border border-transparent'
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                          isSel ? `${theme.selectedIconBg} ${theme.selectedIconText}` : 'bg-white/[0.04] text-white/60'
                        }`}>
                          {renderMethodIcon(method)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-xs sm:text-sm text-white truncate">{method.name}</p>
                          <p className="text-[11px] text-white/40 truncate">{method.description}</p>
                        </div>
                        {isContact ? (
                          <span className="text-[10px] font-semibold text-white/50 bg-white/[0.06] px-2 py-0.5 rounded-md shrink-0">
                            {t('otherMethods') || 'Support'}
                          </span>
                        ) : (
                          <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all shrink-0 ${
                            isSel ? `${theme.radioBorder} ${theme.radioBg}` : 'border-white/20'
                          }`}>
                            {isSel && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* FIXED: everything below methods */}
                <div className="shrink-0">
                  <AnimatePresence>
                    {showSupportMessage && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden mb-3"
                      >
                        <div className="rounded-xl p-3 bg-white/[0.03] border border-white/[0.06] text-center">
                          <p className="font-semibold text-xs text-white mb-0.5">{t('supportMessageTitle')}</p>
                          <p className="text-[11px] text-white/50 mb-2.5 leading-relaxed">{t('supportMessageDescription')}</p>
                          <div className="flex gap-2 justify-center">
                            <a
                              href={SUPPORT_LINKS.discord}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#5865F2]/10 hover:bg-[#5865F2]/20 text-[#8ea1e1] hover:text-[#9eafe6] text-xs font-semibold transition-colors"
                            >
                              <DiscordIcon className="w-3.5 h-3.5" />
                              <span>Discord</span>
                            </a>
                            <a
                              href={SUPPORT_LINKS.telegram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0088cc]/10 hover:bg-[#0088cc]/20 text-[#40b3e6] hover:text-[#52beef] text-xs font-semibold transition-colors"
                            >
                              <TelegramIcon className="w-3.5 h-3.5" />
                              <span>Telegram</span>
                            </a>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <p className="text-[11px] text-white/40 text-center mb-2.5">
                    {t('byProceedingWithPayment')}{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className={`${theme.textAccent} hover:underline underline-offset-2 transition-colors`}>
                      {t('termsOfService')}
                    </a>
                  </p>

                  <button
                    onClick={handleConfirm}
                    disabled={!selectedMethod}
                    className={`w-full min-h-[42px] py-2.5 px-4 rounded-xl text-xs sm:text-sm font-bold transition-all duration-200 flex items-center justify-center cursor-pointer ${
                      selectedMethod
                        ? `${theme.buttonBg} active:scale-[0.98]`
                        : 'bg-white/[0.04] text-white/20 cursor-not-allowed pointer-events-none'
                    }`}
                  >
                    {selectedMethod ? `${t('confirmWith') || 'Pay with'} ${selectedMethod.name}` : (t('selectPaymentMethod') || 'Select payment method')}
                  </button>

                  {internalError && (
                    <div className="mt-2.5 p-2.5 rounded-xl bg-rose-500/[0.08] border border-rose-500/20 text-center">
                      <span className="text-rose-300 text-xs font-medium">{internalError}</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-10 text-white/40">
                <CardIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-xs">{t('noPaymentMethodsAvailable')}</p>
              </div>
            )}
          </motion.div>
        )}

        {/* ── Contact Support ── */}
        {modalPhase === 'contact_support' && (
          <motion.div key="support" {...anim} className="text-center py-2">
            <div className="flex items-center justify-center mx-auto mb-2.5 text-white">
              <SupportIcon className="w-8 h-8 text-white/90" />
            </div>
            <h3 className="text-base font-bold text-white mb-1">{t('otherPaymentMethods')}</h3>
            <p className="text-white/50 text-xs mb-4 max-w-xs mx-auto leading-relaxed">{t('otherPaymentMethodsDescription')}</p>
            
            {/* Текстовые кнопки мессенджеров в стиле аккуратных пилюль */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <a
                href={SUPPORT_LINKS.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-[7px] rounded-full border border-white/10 bg-white/[0.05] text-white text-xs font-semibold inline-flex items-center justify-center transition-all duration-150 select-none cursor-pointer hover:-translate-y-[1px]"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.accent;
                  e.currentTarget.style.borderColor = theme.accent;
                  e.currentTarget.style.color = '#000000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '';
                  e.currentTarget.style.borderColor = '';
                  e.currentTarget.style.color = '';
                }}
              >
                Telegram
              </a>
              <a
                href={SUPPORT_LINKS.discord}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-[7px] rounded-full border border-white/10 bg-white/[0.05] text-white text-xs font-semibold inline-flex items-center justify-center transition-all duration-150 select-none cursor-pointer hover:-translate-y-[1px]"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.accent;
                  e.currentTarget.style.borderColor = theme.accent;
                  e.currentTarget.style.color = '#000000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '';
                  e.currentTarget.style.borderColor = '';
                  e.currentTarget.style.color = '';
                }}
              >
                Discord
              </a>
            </div>

            <div className="flex flex-wrap gap-1.5 justify-center">
              {[t('cryptoCurrency'), t('bankTransfer'), t('otherMethods')].map((m, i) => (
                <span key={i} className="text-[10.5px] text-white/40 bg-white/[0.04] px-2.5 py-0.5 rounded-md font-medium">
                  {m}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {/* ── Initiating ── */}
        {modalPhase === 'initiating_payment' && (
          <motion.div key="init" {...anim} className="py-8 text-center">
            <div
              className="w-10 h-10 border-2 border-white/20 rounded-full animate-spin mx-auto mb-4"
              style={{ borderTopColor: theme.accent }}
            />
            <h3 className="text-base font-bold text-white mb-1">{t('initiatingPayment')}</h3>
            <p className="text-white/50 text-xs">{t('pleaseWait') || 'Processing...'}</p>
          </motion.div>
        )}

        {/* ── Awaiting ── */}
        {modalPhase === 'awaiting_payment_completion' && (
          <motion.div key="await" {...anim} className="text-center py-4">
            <div className="flex items-center justify-center mx-auto mb-2 text-amber-400">
              <ClockIcon className="w-8 h-8 animate-pulse" />
            </div>
            <h3 className="text-base font-bold text-white mb-1">{t('awaitingPaymentConfirmation')}</h3>
            <p className="text-white/50 text-xs mb-4 max-w-xs mx-auto leading-relaxed">{t('paymentWindowOpened')}</p>
            {paymentDetails?.paymentUrl && (
              <a
                href={paymentDetails.paymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl ${theme.buttonBg} font-semibold text-xs mb-1 active:scale-[0.98] transition-all cursor-pointer`}
              >
                <span>{t('openPaymentPage')}</span>
                <ExternalLinkIcon className="w-3.5 h-3.5" />
              </a>
            )}

            <AnimatePresence>
              {showLongWaitSupport && (
                <motion.div
                  initial={{ opacity: 0, height: 0, y: 8 }}
                  animate={{ opacity: 1, height: 'auto', y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -8 }}
                  transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden mt-2"
                >
                  <div className="rounded-xl p-3 bg-white/[0.03] border border-white/[0.06] mx-auto max-w-xs text-center">
                    <p className="text-xs text-white/60 mb-1.5">{t('longWaitQuestion')}</p>
                    <a
                      href={SUPPORT_LINKS.email}
                      className="inline-flex items-center gap-1.5 text-white/90 hover:text-white font-medium text-xs underline underline-offset-2 transition-colors"
                    >
                      {SUPPORT_LINKS.email.replace('mailto:', '')}
                    </a>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* ── Success ── */}
        {modalPhase === 'payment_successful' && (
          <motion.div key="ok" {...anim} className="text-center py-4">
            <div className="flex items-center justify-center mx-auto mb-2 text-emerald-400">
              <CheckIcon className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-white mb-1">{t('paymentConfirmed')}</h3>
            <p className="text-white/50 text-xs mb-4 max-w-xs mx-auto leading-relaxed">
              {internalSuccessMessage || `${t('your')} ${plan.name} ${t('planIsActive')}`}
            </p>
            <div className="rounded-xl p-3 bg-white/[0.03] border border-white/[0.06] mb-4">
              <p className="text-xs font-semibold text-white mb-2">{t('joinOurCommunity')}</p>
              <div className="flex gap-2 justify-center">
                <a
                  href={SUPPORT_LINKS.discord}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#5865F2]/10 hover:bg-[#5865F2]/20 text-[#8ea1e1] hover:text-[#9eafe6] text-xs font-semibold transition-colors"
                >
                  <DiscordIcon className="w-3.5 h-3.5" />
                  <span>Discord</span>
                </a>
                <a
                  href={SUPPORT_LINKS.telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0088cc]/10 hover:bg-[#0088cc]/20 text-[#40b3e6] hover:text-[#52beef] text-xs font-semibold transition-colors"
                >
                  <TelegramIcon className="w-3.5 h-3.5" />
                  <span>Telegram</span>
                </a>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`w-full py-2.5 px-4 rounded-xl text-xs font-bold ${theme.buttonBg} active:scale-[0.98] transition-all cursor-pointer`}
            >
              {t('done')}
            </button>
          </motion.div>
        )}

        {/* ── Error ── */}
        {modalPhase === 'payment_error' && (
          <motion.div key="err" {...anim} className="text-center py-4">
            <div className="flex items-center justify-center mx-auto mb-2 text-rose-400">
              <AlertTriangleIcon className="w-8 h-8 text-rose-400" />
            </div>
            <h3 className="text-base font-bold text-white mb-1">{t('paymentError')}</h3>
            <p className="text-white/50 text-xs mb-4 max-w-xs mx-auto leading-relaxed">{internalError || t('unknownErrorOccurred')}</p>
            <div className="flex gap-2">
              <button
                onClick={handleBack}
                className={`flex-1 py-2.5 px-4 rounded-xl ${theme.buttonBg} text-xs font-bold transition-all active:scale-[0.98] cursor-pointer flex items-center justify-center gap-1.5`}
              >
                <RefreshIcon className="w-3.5 h-3.5" />
                <span>{t('tryAgain') || 'Retry'}</span>
              </button>
              <button
                onClick={onClose}
                className="py-2.5 px-4 rounded-xl bg-white/[0.05] hover:bg-white/[0.09] text-xs font-semibold text-white/70 hover:text-white transition-all cursor-pointer"
              >
                {t('close') || 'Close'}
              </button>
            </div>
          </motion.div>
        )}

      </div>
    </div>
  );
};

export default PaymentMethodsModal;